import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthReady } from "@/hooks/useAuthReady";
import { useActiveOrg } from "@/hooks/useActiveOrg";

/**
 * Resolves the signed-in user's primary org id by reading the most recent
 * non-deactivated row in `public.organization_members`.
 *
 * The DB columns `joined_at` and `deactivated_at` are owned by the origin
 * (hibane-desk) project and are not yet present in our generated types,
 * so the query is issued via an untyped client cast.
 */
export function usePrimaryOrgId() {
  const { userId, isReady } = useAuthReady();
  // Prefer the ActiveOrgProvider when mounted so the user's org switcher
  // selection drives every consumer of this hook.
  const active = useActiveOrg();
  const hasProvider = active.memberships.length > 0 || active.isLoading;

  const query = useQuery({
    queryKey: ["primary-org-id", userId],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<string | null> => {
      if (!userId) return null;
      try {
        const client = supabase as unknown as {
          from: (t: string) => any;
        };
        const { data, error } = await client
          .from("organization_members")
          .select("org_id, created_at")
          .is("deactivated_at", null)
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) {
          // Surface unexpected RLS regressions (e.g. 42P17 recursion) without
          // locking the user out — useEffectiveSuperAdmin tolerates a null orgId.
          // eslint-disable-next-line no-console
          console.warn("[usePrimaryOrgId] organization_members read failed", error);
          return null;
        }
        return (data?.org_id as string) ?? null;
      } catch {
        return null;
      }
    },
  });

  // Defensive fallback: if the user has no organization_members row but is
  // a global staff super admin (`has_role(_, 'super_admin') === true`), they
  // must still be gated as super-admin. This prevents the same lockout we
  // hit for ops accounts that were never added to an org.
  const superQuery = useQuery({
    queryKey: ["primary-org-id-super-fallback", userId],
    enabled: !!userId && query.isFetched && (query.data ?? null) === null,
    staleTime: 60_000,
    queryFn: async (): Promise<boolean> => {
      if (!userId) return false;
      try {
        const rpc = supabase.rpc as unknown as (
          fn: string,
          args: Record<string, unknown>,
        ) => Promise<{ data: unknown; error: unknown }>;
        const { data, error } = await rpc("has_role", {
          _user_id: userId,
          _role: "super_admin",
        });
        return !error && data === true;
      } catch {
        return false;
      }
    },
  });

  return {
    orgId: hasProvider ? active.orgId : (query.data ?? null),
    isLoading: hasProvider
      ? active.isLoading
      :
      !isReady ||
      (!!userId && query.isLoading) ||
      (!!userId && (query.data ?? null) === null && superQuery.isLoading),
    isGlobalSuperAdmin: hasProvider ? active.isGlobalSuperAdmin : superQuery.data === true,
  };
}

export default usePrimaryOrgId;